import { OrganizationStatus } from "@graphql";
import { KeyboardArrowDown } from "@mui/icons-material";
import { Box, Button, Checkbox, Divider, ListItemText, ListSubheader, MenuItem, OutlinedInput, Popover, Radio, Select, SxProps, Typography } from "@mui/material";
import { COLORS } from "@styles";
import { observer } from "mobx-react";
import { Dispatch, SetStateAction, useState } from "react";
import GridViewIcon from '@mui/icons-material/GridView';
import { GridDensity } from "@mui/x-data-grid";
import { AccountType, BillingIntervalSelector, BillingMethod, FilterProps, BillingStatus, TableColumns, PlanType } from "../../pages/Organizations/_/AllOrganizationsTable/index";

const INPUT_STYLE: SxProps = { border: `1px solid ${COLORS.NEUTRAL_DARK_2}`, borderRadius: "1rem", display: "flex", width: "10rem" };
const BUTTON_STYLE: SxProps = { width: "100%", fontSize: "0.875rem", borderRadius: "0.5rem" };
const LIST_SUBHEADER_STYLE: SxProps = { color: COLORS.NEUTRAL_WHITE, backgroundColor: COLORS.NEUTRAL_DARK_2, textTransform: "uppercase", lineHeight: "normal", padding: "0.5rem 1rem", fontSize: "0.625rem" };
const MENU_ITEM_STYLE: SxProps = { padding: "0 1rem", gap: "1rem", };
const MENU_ITEM_TEXT_STYLE: SxProps = { fontSize: "0.75rem", color: COLORS.NEUTRAL_WHITE };

// ===============================================
// Organization Status Selector
// ===============================================
interface OrganizationSelectorProps {
  value: FilterProps["orgStatus"];
  setValue: Dispatch<SetStateAction<FilterProps>>;
}

const valuesOptions: { label: string; value: FilterProps["orgStatus"] }[] = [
  {
    label: "All",
    value: "All",
  },
  {
    label: "Enabled",
    value: OrganizationStatus.Active,
  },
  {
    label: "Disabled",
    value: OrganizationStatus.Inactive,
  },
];

export const OrganizationStatusSelector = observer(({ value, setValue }: OrganizationSelectorProps) => (
  <Select
    input={<OutlinedInput sx={INPUT_STYLE} />}
    IconComponent={KeyboardArrowDown}
    MenuProps={{ PaperProps: { sx: { background: COLORS.NEUTRAL_DARK_2 } }, disablePortal: true }}
    size='small'
    variant='outlined'
    displayEmpty
    value={value}
    onChange={(e) => setValue(prev => ({ ...prev, orgStatus: e.target.value as FilterProps["orgStatus"] }))}
    renderValue={() => "Org. Status"}
  >
    {valuesOptions.map((option) => (
      <MenuItem key={option.value} sx={{ padding: "0 1rem", gap: "1rem" }} value={option.value}>
        <Radio checked={value === option.value} />
        <ListItemText primary={option.label} />
      </MenuItem>
    ))}
  </Select>
));

// ===============================================
// Organization Account Type Selector (Demo vs Paying)
// ===============================================
interface OrganizationAccountTypeSelectorProps {
  value: FilterProps["accountType"];
  setValue: Dispatch<SetStateAction<FilterProps>>;
}

const accountTypeOptions: { label: string; value: AccountType }[] = [
  { label: "All", value: AccountType.ALL },
  { label: "Demo accounts", value: AccountType.DEMO },
  { label: "Paying customers", value: AccountType.PAYING },
];

export const OrganizationAccountTypeSelector = observer(({ value, setValue }: OrganizationAccountTypeSelectorProps) => (
  <Select
    input={<OutlinedInput sx={INPUT_STYLE} />}
    IconComponent={KeyboardArrowDown}
    MenuProps={{ PaperProps: { sx: { background: COLORS.NEUTRAL_DARK_2 } }, disablePortal: true }}
    size='small'
    variant='outlined'
    displayEmpty
    value={value}
    onChange={(e) => setValue(prev => ({ ...prev, accountType: e.target.value as AccountType }))}
    renderValue={() => "Account Type"}
  >
    {accountTypeOptions.map((option) => (
      <MenuItem key={option.value} sx={{ padding: "0 1rem", gap: "1rem" }} value={option.value}>
        <Radio checked={value === option.value} />
        <ListItemText primary={option.label} />
      </MenuItem>
    ))}
  </Select>
));

// ===============================================
// Organization Plan And Billing Selector
// ===============================================
interface OrganizationPlanAndBillingSelectorProps {
  setValue: Dispatch<SetStateAction<FilterProps>>;
  value: FilterProps["planAndBilling"];
}

export type PlanAndBillingValue = PlanType | BillingStatus | BillingMethod | BillingIntervalSelector;

interface OrganizationPlanAndBillingSelectorItemProps {
  options: { label: string; value: PlanAndBillingValue }[];
  temporaryValue: FilterProps["planAndBilling"];
  fieldKey: keyof FilterProps["planAndBilling"];
  listSubheader: string;
  setTemporaryValue: Dispatch<SetStateAction<FilterProps["planAndBilling"]>>;
}

const planTypeOptions = [{ label: "All", value: PlanType.ALL }, { label: "Enterprise", value: PlanType.ENTERPRISE }, { label: "Pro", value: PlanType.PRO }, { label: "Core", value: PlanType.CORE }];

const billingStatusOptions = [{ label: "All", value: BillingStatus.ALL }, { label: "Active", value: BillingStatus.ACTIVE }, { label: "Cancelled", value: BillingStatus.CANCELLED }, { label: "Trialing", value: BillingStatus.TRIALING }];

const billingMethod = [{ label: "All", value: BillingMethod.ALL }, { label: "Credit Card", value: BillingMethod.CREDIT_CARD }, { label: "Invoice", value: BillingMethod.INVOICE }];

const billingInterval = [{ label: "All", value: BillingIntervalSelector.ALL }, { label: "Monthly", value: BillingIntervalSelector.MONTHLY }, { label: "Annual", value: BillingIntervalSelector.YEARLY },];

export const ALL_OPTIONS_VALUES = [PlanType.ALL, BillingStatus.ALL, BillingMethod.ALL, BillingIntervalSelector.ALL];

const OrganizationPlanAndBillingSelectorItem = observer(({ options, temporaryValue, fieldKey, listSubheader, setTemporaryValue }: OrganizationPlanAndBillingSelectorItemProps) => {


  const selectAllCheckBox = (enumValue: PlanAndBillingValue[]) => {
    setTemporaryValue(prev => ({ ...prev, [fieldKey]: enumValue }))
  }


  const switchCheckBoxesSection = (fieldKeyToSwitch: keyof FilterProps["planAndBilling"]) => {
    switch (fieldKeyToSwitch) {
      case "planType":
        selectAllCheckBox(planTypeOptions.map(option => option.value));
        break;
      case "billingStatus":
        selectAllCheckBox(billingStatusOptions.map(option => option.value));
        break;
      case "billingMethod":
        selectAllCheckBox(billingMethod.map(option => option.value));
        break;
      case "billingInterval":
        selectAllCheckBox(billingInterval.map(option => option.value));
        break;
      default:
        break;
    }
  }

  const onChange = (optionValue: PlanAndBillingValue) => {
    const totalOptions = options.length;

    // Uncheck and Check All checkbox
    if (ALL_OPTIONS_VALUES.includes(optionValue)) {
      const isAllSelected = temporaryValue?.[fieldKey]?.some(type => type === optionValue);
      if (isAllSelected) {
        setTemporaryValue(prev => ({ ...prev, [fieldKey]: [] }));
        return
      }
      switchCheckBoxesSection(fieldKey);
      return
    }

    // Uncheck specific checkbox
    if ((temporaryValue?.[fieldKey] as PlanAndBillingValue[])?.includes(optionValue)) {
      const newValues = temporaryValue?.[fieldKey]?.filter(type => type !== optionValue && !ALL_OPTIONS_VALUES.includes(type as PlanAndBillingValue)) || [];
      setTemporaryValue(prev => ({ ...prev, [fieldKey]: newValues }));
      return
    }

    const newValues = [...(temporaryValue?.[fieldKey] || []), optionValue];

    // Check All checkbox
    if (newValues.length === totalOptions - 1) {
      switchCheckBoxesSection(fieldKey);
      return
    }

    // Check specific checkbox
    setTemporaryValue(prev => ({ ...prev, [fieldKey]: newValues }));
  };

  return (
    <>
      <ListSubheader sx={LIST_SUBHEADER_STYLE}>{listSubheader}</ListSubheader>
      {options.map(({ label, value: optionValue }) => (
        <MenuItem key={optionValue} onClick={() => onChange(optionValue)} sx={MENU_ITEM_STYLE} value={optionValue}>
          <Checkbox value={optionValue} checked={(temporaryValue?.[fieldKey] as PlanAndBillingValue[])?.includes(optionValue) ?? false} />
          <ListItemText primary={label} primaryTypographyProps={{ sx: MENU_ITEM_TEXT_STYLE }} />
        </MenuItem>
      ))}
    </>
  );
});


export const OrganizationPlanAndBillingSelector = observer(({ setValue, value }: OrganizationPlanAndBillingSelectorProps) => {
  const [temporaryValue, setTemporaryValue] = useState<FilterProps["planAndBilling"]>(value);

  const onApply = () => {
    setValue(prev => ({ ...prev, planAndBilling: temporaryValue }));
  };

  const onClear = () => {
    setValue(prev => ({ ...prev, planAndBilling: {} as FilterProps["planAndBilling"] }));
    setTemporaryValue({} as FilterProps["planAndBilling"]);
  };

  return (
    <Select
      size='small'
      input={<OutlinedInput sx={INPUT_STYLE} />}
      IconComponent={KeyboardArrowDown}
      renderValue={() => "Plan & Billing"}
      displayEmpty
      multiple
      value={[...(temporaryValue.planType ?? []), ...(temporaryValue.billingStatus ?? []), ...(temporaryValue.billingMethod ?? []), ...(temporaryValue.billingInterval ?? [])]}
      MenuProps={{ PaperProps: { sx: { background: COLORS.NEUTRAL_DARK_2, width: "17rem" } } }}
    >
      <OrganizationPlanAndBillingSelectorItem options={planTypeOptions} temporaryValue={temporaryValue} fieldKey="planType" listSubheader="Plan Type" setTemporaryValue={setTemporaryValue} />
      <OrganizationPlanAndBillingSelectorItem options={billingStatusOptions} temporaryValue={temporaryValue} fieldKey="billingStatus" listSubheader="Billing Status" setTemporaryValue={setTemporaryValue} />
      <OrganizationPlanAndBillingSelectorItem options={billingMethod} temporaryValue={temporaryValue} fieldKey="billingMethod" listSubheader="billing method" setTemporaryValue={setTemporaryValue} />
      <OrganizationPlanAndBillingSelectorItem options={billingInterval} temporaryValue={temporaryValue} fieldKey="billingInterval" listSubheader="Subscription frequency" setTemporaryValue={setTemporaryValue} />

      <Box display='grid' gridRow={2} gridColumn={2} px='1rem' rowGap='1rem' columnGap='0.5rem'>
        <Divider sx={{ gridRow: 1, gridColumn: 'span 2' }} />
        <Button sx={{ ...BUTTON_STYLE, backgroundColor: COLORS.ELEKTRAFI_NEUTRAL, color: COLORS.NEUTRAL_WHITE }} onClick={onApply}>Apply</Button>
        <Button sx={{ ...BUTTON_STYLE, backgroundColor: "transparent", color: COLORS.NEUTRAL_WHITE, border: `1px solid ${COLORS.NEUTRAL_LIGHT_1}` }} onClick={onClear}>Clear</Button>
      </Box>
    </Select >
  );
});


// ===============================================
// Organization Team Metrics Selector
// ===============================================
interface OrganizationTeamMetricsSelectorProps {
  setValue: Dispatch<SetStateAction<FilterProps>>;
  value: FilterProps["teamMetrics"];
}

export const OrganizationTeamMetricsSelector = observer(({ setValue, value }: OrganizationTeamMetricsSelectorProps) => {
  const [temporaryValue, setTemporaryValue] = useState<FilterProps["teamMetrics"]>(value);

  const onApply = () => {
    setValue(prev => ({ ...prev, teamMetrics: temporaryValue }));
  };

  const onClear = () => {
    setValue(prev => ({ ...prev, teamMetrics: {} as FilterProps["teamMetrics"] }));
    setTemporaryValue({ minEmployees: 0, maxEmployees: 0 });
  };

  return (
    <Select
      size='small'
      input={<OutlinedInput sx={INPUT_STYLE} />}
      IconComponent={KeyboardArrowDown}
      renderValue={() => "Team Metrics"}
      displayEmpty
      MenuProps={{ PaperProps: { sx: { background: COLORS.NEUTRAL_DARK_2, width: "17rem" } } }}
    >
      <Box sx={{ padding: '1rem' }} display='flex' flexDirection='column' gap='1rem'>
        <Box display='flex' flex={1} gap='0.5rem'>
          <Box display='flex' flexDirection='column' flex={1} gap='0.25rem'>
            <Typography sx={{ marginLeft: "1rem", fontSize: "0.75rem", fontWeight: 600 }}>Min</Typography>
            <OutlinedInput size='medium' type='number' style={{ width: '100%' }} sx={INPUT_STYLE} value={temporaryValue.minEmployees || 0} onChange={(e) => setTemporaryValue(prev => ({ ...prev, minEmployees: +(e.target.value) }))} />
          </Box>
          <Box display='flex' flexDirection='column' flex={1} gap='0.25rem'>
            <Typography sx={{ marginLeft: "1rem", fontSize: "0.75rem", fontWeight: 600 }}>Max</Typography>
            <OutlinedInput size='medium' type='number' style={{ width: '100%' }} sx={INPUT_STYLE} value={temporaryValue.maxEmployees || 0} onChange={(e) => setTemporaryValue(prev => ({ ...prev, maxEmployees: +(e.target.value) }))} />
          </Box>
        </Box>
        <Box display='grid' gridRow={2} gridColumn={2} rowGap='1rem' columnGap='0.5rem'>
          <Divider sx={{ gridRow: 1, gridColumn: 'span 2' }} />
          <Button sx={{ ...BUTTON_STYLE, backgroundColor: COLORS.ELEKTRAFI_NEUTRAL, color: COLORS.NEUTRAL_WHITE }} onClick={onApply}>Apply</Button>
          <Button sx={{ ...BUTTON_STYLE, backgroundColor: "transparent", color: COLORS.NEUTRAL_WHITE, border: `1px solid ${COLORS.NEUTRAL_LIGHT_1}` }} onClick={onClear}>Clear</Button>
        </Box>
      </Box>
    </Select >
  )
});

// ===============================================
// Organization Columns Selector
// ===============================================
interface OrganizationColumnsSelectorProps {
  setValue: Dispatch<SetStateAction<FilterProps>>;
  value: FilterProps["tableSettings"];
}

const COLLUMNS_OPTIONS: { label: string; value: TableColumns }[] = [{ label: "ID", value: "id" }, { label: "Company Name", value: "name" }, { label: "Plan", value: "organizationPlan" }, { label: "Renewal Date", value: "renewalDate" }, { label: "Employees", value: "employees" }, { label: "Seats Usage", value: "seatsUsage" }, { label: "Manager", value: "admin" }, { label: "Advisor", value: "advisor" }];

const DENSITY_OPTIONS: { label: string; value: GridDensity }[] = [{ label: "Compact", value: "compact" }, { label: "Standard", value: "standard" }, { label: "Comfortable", value: "comfortable" }];

export const OrganizationColumnsSelector = observer(({ setValue, value }: OrganizationColumnsSelectorProps) => {
  const [temporaryValue, setTemporaryValue] = useState<FilterProps["tableSettings"]>(value);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const toggleOpen = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);

  const onChange = (optionValue: TableColumns) => {
    if (temporaryValue.columns.includes(optionValue)) {
      setTemporaryValue(prev => ({ ...prev, columns: prev.columns.filter(column => column !== optionValue) }));
      return
    }
    setTemporaryValue(prev => ({ ...prev, columns: [...prev.columns, optionValue] }));
  }

  const onClose = () => {
    setValue(prev => ({ ...prev, tableSettings: temporaryValue }));
    setAnchorEl(null);
  };

  return (
    <Box position="relative" display="flex" alignItems="center" justifyContent="center">
      <Button component="button" sx={{ padding: 0, minWidth: 0 }} onClick={(event: React.MouseEvent<HTMLButtonElement>) => toggleOpen(event)}>
        <GridViewIcon sx={{ color: "#90CAF9" }} />
      </Button>
      <Popover
        PaperProps={{
          sx: {
            width: "17rem", background: COLORS.NEUTRAL_DARK_2, padding: "1rem", borderRadius: "1rem"
          }
        }}
        open={Boolean(anchorEl)}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }} anchorEl={anchorEl} >
        <ListSubheader sx={{ ...LIST_SUBHEADER_STYLE, padding: "0" }}>Columns</ListSubheader>
        {COLLUMNS_OPTIONS.map(({ label, value: optionValue }) => (
          <MenuItem key={optionValue} sx={{ ...MENU_ITEM_STYLE, padding: "0" }} value={optionValue} onClick={() => onChange(optionValue)}>
            <Checkbox value={optionValue} checked={temporaryValue.columns.includes(optionValue)} />
            <ListItemText primary={label} primaryTypographyProps={{ sx: MENU_ITEM_TEXT_STYLE }} />
          </MenuItem>
        ))}

        <Divider />

        <Box display='flex' flexDirection='column'>
          <Typography sx={{ marginLeft: "1rem", fontSize: "0.75rem", fontWeight: 600 }}>Density</Typography>
          <Select
            size='medium'
            input={<OutlinedInput sx={{ ...INPUT_STYLE, width: "100%" }} />}
            IconComponent={KeyboardArrowDown}
            renderValue={() => temporaryValue.tableDensity}
            displayEmpty
            value={temporaryValue.tableDensity}
            onChange={(e) => setTemporaryValue(prev => ({ ...prev, tableDensity: e.target.value as GridDensity }))}
            MenuProps={{ PaperProps: { sx: { background: COLORS.NEUTRAL_DARK_2, width: "17rem" } } }}
          >
            {DENSITY_OPTIONS.map(({ label, value: optionValue }) => (
              <MenuItem key={optionValue} value={optionValue}>{label}</MenuItem>
            ))}
          </Select>
        </Box>
      </Popover >
    </Box >
  )
});